import { observer } from 'mobx-react';
import { useAuthContext } from '../../components/AuthProvider';
import { useEffect, useState } from 'react';
import { useAnalysisSchemasQuery } from '../../api/queries/analysisSchemas';
import { useStateLocalStorage } from '../../hooks/useStateLocalStorage';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Modal } from '@convergence/components';
import { FileBrowserSelectionType, ValueType } from '@convergence/types';
import {
  AnalysisInputProps,
  AnalysisInputs
} from '../../components/analysis-value/input/analysis-inputs';
import { useNavigate } from 'react-router';
import {
  LabFileBrowser,
  useLabFileBrowser
} from '../../components/lab-file-browser';
import { useCreateAndLaunchAnalysisMutation } from '../../api/queries/createAndLaunchAnalysis';
import { Folder } from '@phosphor-icons/react';
import { useErrorToast } from '../../lib/useErrorToast';
import { useAnalysisSchemaQuery } from '../../api/queries/analysisSchema';
import { AnalysisSchemaSelector } from '../../components/AnalysisSchemaSelector';
import { AnalysisSchemaDtoType } from '@convergence/client';

export const Home = observer(() => {
  const { user } = useAuthContext();
  const [selectedSchemaAndVersion, setSelectedSchemaAndVersion] = useState<{
    schema: AnalysisSchemaDtoType;
    version: number;
  } | null>(null);

  console.log({ selectedSchemaAndVersion });

  const analysisSchemasQuery = useAnalysisSchemasQuery();
  const analysisSchemaQuery = useAnalysisSchemaQuery(
    selectedSchemaAndVersion?.schema.id,
    selectedSchemaAndVersion?.version
  );

  useErrorToast(analysisSchemasQuery.error);

  const createAndLaunchAnalysisMutation = useCreateAndLaunchAnalysisMutation();

  const navigate = useNavigate();

  const selectedSchemaVersion = analysisSchemaQuery.data?.versions.find(
    (v) => v.version === selectedSchemaAndVersion?.version
  );
  const randomStarWarsCharacterQuery = useQuery({
    queryKey: ['randomStarWarsCharacter'],
    queryFn: async () => {
      const response = await fetch(
        'https://random-star-wars-character.vercel.app/api',
        { method: 'GET' }
      );
      const data = await response.text();
      return data;
    }
  });

  const [analysisName, setAnalysisName] = useStateLocalStorage('analysisName', {
    touched: false,
    value: randomStarWarsCharacterQuery.data || ''
  });

  useEffect(() => {
    if (randomStarWarsCharacterQuery.data && !analysisName.touched) {
      setAnalysisName({
        touched: false,
        value: randomStarWarsCharacterQuery.data
      });
    }
  }, [randomStarWarsCharacterQuery.data]);

  const [outputLocation, setOutputLocation] = useStateLocalStorage<{
    bucketId?: number;
    path: string;
    isFolder: boolean;
    labName: string;
  } | null>('outputLocation', null);
  const [showOutputLocationModal, setShowOutputLocationModal] = useState(false);

  const fileBrowserState = useLabFileBrowser();

  return (
    <div className="h-full overflow-scroll p-8 bg-white">
      {analysisSchemasQuery.error && (
        <div>
          Error loading analysis schemas:{' '}
          {(analysisSchemasQuery.error as Error).message}
        </div>
      )}
      <div className="grid grid-rows-3 grid-cols-1 mb-4 justify-center items-start ">
        <div className="flex gap-4 border-b border-black items-center w-full h-full border-l border-t p-4 border-r">
          <AnalysisSchemaSelector
            onChange={(schema, version) => {
              console.log({ schema, version });
              setSelectedSchemaAndVersion({ schema, version });
            }}
          />
        </div>
        <div className="flex gap-4 border-b border-black items-center h-full border-l p-4 border-r">
          <label>Name:</label>
          <Input
            onChange={(e) =>
              setAnalysisName({ touched: true, value: e.target.value })
            }
            value={analysisName.value}
          />
        </div>
        <div className="flex gap-4 items-center h-full border-b border-black border-l p-4 border-r">
          <label>Output Location:</label>
          <Button
            onClick={() => setShowOutputLocationModal(true)}
            className="flex gap-2 items-center self-baseline"
          >
            <Folder className="inline" />{' '}
            {outputLocation
              ? `${outputLocation.labName}://${outputLocation.path}/`
              : 'Select Output Location'}
          </Button>
        </div>
        <Modal
          visible={showOutputLocationModal}
          onClose={() => setShowOutputLocationModal(false)}
          className=""
        >
          <div className="p-4 h-full overflow-hidden flex flex-col">
            <LabFileBrowser
              path={fileBrowserState.path}
              setPath={fileBrowserState.setPath}
              selection={outputLocation ? [outputLocation] : []}
              pathType={FileBrowserSelectionType.DIR}
              bucketId={fileBrowserState.bucketId}
              setBucketId={fileBrowserState.setBucketId}
              onSelectionChange={(selection, lab) => {
                if (selection.length > 0) {
                  setOutputLocation({
                    ...selection[0]!,
                    labName: lab.name
                  });
                }
              }}
            />
            <Button
              className="mt-4"
              onClick={() => setShowOutputLocationModal(false)}
            >
              Select
            </Button>
          </div>
        </Modal>
      </div>

      <div className="border-black border p-4">
        {selectedSchemaVersion && selectedSchemaAndVersion?.schema && (
          <AnalysisInputs
            inputs={
              selectedSchemaVersion.inputs as AnalysisInputProps['inputs']
            }
            onSubmit={async (input) => {
              if (!outputLocation) {
                return;
              }
              const res = await createAndLaunchAnalysisMutation.mutateAsync({
                input: Object.entries(input).reduce(
                  (acc, [key, value]) => {
                    // remove errors and get schema
                    acc[+key] = value.schema;
                    return acc;
                  },
                  {} as Record<number, ValueType>
                ),
                outputLocation: {
                  s3BucketId: outputLocation!.bucketId!, // TODO: check for null
                  path: outputLocation!.path
                },

                userId: user!.id,
                analysisSchemaId: selectedSchemaAndVersion.schema.id,
                version: selectedSchemaVersion.version,
                name: analysisName.value
              });
              if (res.analysisId) {
                navigate(`/a/${res.analysisSlug}`);
              }
            }}
          />
        )}
      </div>
      {createAndLaunchAnalysisMutation.error && (
        <div className="text-red-500">
          Error launching analysis:{' '}
          {(createAndLaunchAnalysisMutation.error as Error).message}
        </div>
      )}
    </div>
  );
});
